// Compressing a file before it goes up the wire.
//
// Scans are the one thing this lab moves that is both large and compressible. An ASCII STL is
// repetitive text and loses well over ninety percent; a binary STL still loses a third, because
// the vertices repeat and the two-byte attribute on every triangle is always zero. On a typical
// office uplink that is the difference between a six-minute upload and a two-minute one, and it
// costs about a second of the phone's or laptop's CPU.
//
// Two rules keep it honest:
//   • only formats that actually compress — running gzip over a JPEG or an MP4 burns CPU and
//     memory to produce something fractionally bigger;
//   • only send the compressed version if it genuinely came out smaller. If the file was already
//     dense, the original goes as it always did.

/** Formats worth compressing: text-ish or structurally repetitive. */
const COMPRESSIBLE = new Set(['stl', 'obj', 'ply', 'json', 'gltf', 'txt', 'csv', 'xml', 'dcm', '3mf']);

/** Already compressed — squeezing them again is pure loss. */
const ALREADY_DENSE = new Set(['zip', 'rar', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'mp4', 'mov', 'webm', 'pdf', 'glb', 'xlsx', 'docx']);

/** Below this, the round trip through gzip costs more than the bytes it saves. */
const MIN_BYTES = 512 * 1024;

/** Not worth the CPU if it only shaves a few percent. */
const WORTH_IT = 0.92;

const extOf = (name: string) => (name.split('.').pop() ?? '').toLowerCase();

export interface PreparedUpload {
  /** What to send: either the original file or a gzipped blob of it. */
  body: Blob;
  /** True when `body` is gzipped and the server must expand it. */
  gzipped: boolean;
  originalBytes: number;
  sentBytes: number;
}

/**
 * Gzip a file when that will help.
 *
 * Never throws: compression is an optimisation, and a browser without CompressionStream, or one
 * that runs out of memory on a 400 MB scan, must still be able to upload — it just uploads the
 * file as it is.
 */
export async function prepareUpload(file: File): Promise<PreparedUpload> {
  const plain: PreparedUpload = { body: file, gzipped: false, originalBytes: file.size, sentBytes: file.size };
  const ext = extOf(file.name);

  if (file.size < MIN_BYTES) return plain;
  if (ALREADY_DENSE.has(ext)) return plain;
  if (!COMPRESSIBLE.has(ext)) return plain;
  if (typeof CompressionStream === 'undefined') return plain;

  try {
    const stream = file.stream().pipeThrough(new CompressionStream('gzip'));
    const gz = await new Response(stream).blob();
    // A file that barely shrank is not worth the server unwrapping it.
    if (gz.size >= file.size * WORTH_IT) return plain;
    return { body: gz, gzipped: true, originalBytes: file.size, sentBytes: gz.size };
  } catch {
    // Out of memory, or an engine that half-implements the API. Send it as it is.
    return plain;
  }
}
